import { Button, cn } from "@simpro/ui";
import { useEffect } from "react";
import { Link } from "react-router";
import { useSalonMoney } from "@/features/salon";
import { ArrowDisc } from "@/shared/components/ArrowDisc";
import { useShellStore } from "@/shared/layout/shell.store";
import { pill } from "@/shared/styles/pill";
import { formatDuration } from "../lib/formatDuration";
import { menuTotal } from "../lib/menuTotal";
import type { PublicCatalog } from "../schemas/booking.schemas";

export interface BasketBarProps {
  catalog: PublicCatalog;
  selected: readonly string[];
  /** e.g. `lg:hidden` where a side panel shows the basket on a desktop. */
  className?: string;
}

/**
 * What the customer has added from the menu, and the way into booking it (TDD-SA FR-SA-138). It hands
 * the services over through `?services=`, the parameter "book again" already uses, so the booking
 * flow opens with them ticked and in order. It rises in the thumb zone once the first service is
 * added and takes the floating Book button's place (design note S1, H1); nothing when empty.
 * Premium redesign (design note salon-booking-premium-redesign.md section 5.3 items 8 and 15): an ink
 * pill with the one soft shadow, figures in tabular numbers, and Continue as the accent.
 */
export function BasketBar({ catalog, selected, className }: BasketBarProps) {
  const formatMoney = useSalonMoney();
  const setBasketOpen = useShellStore((state) => state.setBasketOpen);
  const open = selected.length > 0;

  useEffect(() => {
    setBasketOpen(open);
    return () => setBasketOpen(false);
  }, [open, setBasketOpen]);

  if (!open) return null;

  const services = catalog
    .flatMap((category) => category.services)
    .filter((service) => selected.includes(service.id));
  const minutes = services.reduce((sum, service) => sum + service.durationMinutes, 0);
  const total = menuTotal(services.map((service) => service.price));
  const count = `${selected.length} ${selected.length === 1 ? "service" : "services"}`;

  return (
    <div
      className={cn(
        "pointer-events-none sticky bottom-[calc(1.25rem+env(safe-area-inset-bottom))] z-20 mx-auto flex w-full max-w-xl px-4",
        className,
      )}
    >
      <div
        role="status"
        className="animate-rise on-ink shadow-bar pointer-events-auto flex w-full items-center justify-between gap-4 rounded-full bg-ink py-2 pr-2 pl-5 text-on-ink"
      >
        <span className="min-w-0 text-sm tabular-nums">
          <b className="font-semibold">{count}</b>
          {total !== null ? ` · ${formatMoney(total)}` : ""}
          <span className="text-on-ink/70"> · {formatDuration(minutes)}</span>
        </span>
        <Button asChild className={cn(pill.base, pill.accent, pill.arrow, "h-12 gap-3 pr-1.5 pl-5 text-sm")}>
          <Link to={`/book?services=${selected.map(encodeURIComponent).join(",")}&step=stylist`}>
            Continue
            <ArrowDisc />
          </Link>
        </Button>
      </div>
    </div>
  );
}
